import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  GOOGLE_REVIEW_STATS,
  GOOGLE_REVIEWS,
  type GoogleReview,
} from '@/constants/googleReviews';
import { buildGoogleReviewsJsonLd } from '@/lib/schema/googleReviews';

/**
 * GoogleReviewsCarousel — the single canonical reviews surface for the site.
 *
 * Visible on every public route, directly below the sticky header. Rendered
 * as a CSS marquee (not a stepped embla carousel) so it loops smoothly and
 * pauses on hover. `prefers-reduced-motion: reduce` switches it to a static
 * horizontally scrollable row with momentum scroll.
 *
 * Phase 2A deliverables:
 *  - Replaces the deceptive "What Our Customers Say" partner block.
 *  - Google-native visual identity (G mark, Google palette, Google Sans fallback).
 *  - Emits Review + AggregateRating JSON-LD that matches the on-screen numbers.
 *  - Single source of truth: src/constants/googleReviews.ts
 */

const GoogleGMark: React.FC<{ className?: string; title?: string }> = ({
  className = '',
  title = 'Google',
}) => {
  // Empty title = decorative usage (icon paired with visible text);
  // mark the SVG aria-hidden rather than emit an empty aria-label which
  // axe flags as a serious svg-img-alt violation.
  const decorative = title === '';
  return (
    <svg
      viewBox="0 0 48 48"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      {...(decorative
        ? { 'aria-hidden': true as const, focusable: false as const }
        : { role: 'img' as const, 'aria-label': title })}
    >
      {!decorative && <title>{title}</title>}
      <path
        fill="#4285F4"
        d="M43.6 20.5H42V20H24v8h11.3c-1.7 4.7-6.1 8-11.3 8a12 12 0 1 1 7.9-21.1l5.7-5.7A20 20 0 1 0 44 24c0-1.2-.1-2.4-.4-3.5z"
      />
      <path
        fill="#34A853"
        d="M6.3 14.7l6.6 4.8A12 12 0 0 1 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7A20 20 0 0 0 6.3 14.7z"
      />
      <path
        fill="#FBBC05"
        d="M24 44c5.2 0 9.9-1.8 13.6-4.9l-6.3-5.2A12 12 0 0 1 12.7 28.3l-6.6 5.1A20 20 0 0 0 24 44z"
      />
      <path
        fill="#EA4335"
        d="M43.6 20.5H42V20H24v8h11.3a12 12 0 0 1-4 5.9l6.3 5.2c-.4.4 6.7-4.9 6.7-15.1 0-1.2-.1-2.4-.4-3.5z"
      />
    </svg>
  );
};

const StarIcon: React.FC<{ filled?: boolean }> = ({ filled = true }) => (
  <svg
    viewBox="0 0 20 20"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    className="w-4 h-4 shrink-0"
    fill={filled ? '#FBBC05' : '#E5E7EB'}
  >
    <path d="M10 1.5l2.6 5.3 5.9.8-4.3 4.1 1 5.8L10 14.8l-5.2 2.7 1-5.8L1.5 7.6l5.9-.8L10 1.5z" />
  </svg>
);

/**
 * Deterministic monogram avatar using a name-hash -> hue mapping. Keeps the
 * cards visually consistent with the Google UI when a profile photo isn't
 * available (Places API reviews don't always include one).
 */
const MonogramAvatar: React.FC<{ name: string }> = ({ name }) => {
  const initials = useMemo(() => {
    const parts = name.trim().split(/\s+/);
    const first = parts[0]?.[0] ?? '?';
    const last = parts[1]?.[0] ?? '';
    return (first + last).toUpperCase();
  }, [name]);

  const hue = useMemo(() => {
    let h = 0;
    for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
    return h % 360;
  }, [name]);

  return (
    <div
      aria-hidden="true"
      className="w-9 h-9 rounded-full flex items-center justify-center font-medium text-white text-sm shrink-0"
      style={{ background: `hsl(${hue}, 42%, 48%)` }}
    >
      {initials}
    </div>
  );
};

const truncate = (text: string, max = 180) => {
  if (text.length <= max) return { body: text, truncated: false };
  const cut = text.slice(0, max).replace(/\s+\S*$/, '');
  return { body: cut + '…', truncated: true };
};

const ReviewCard: React.FC<{ review: GoogleReview }> = ({ review }) => {
  const { body } = truncate(review.text, 180);
  return (
    <article
      className="
        flex-none w-[320px] md:w-[360px] bg-white rounded-xl shadow-[0_1px_3px_rgba(60,64,67,0.15)]
        border border-[#e8eaed] p-5 mx-3 flex flex-col gap-3
        focus-within:ring-2 focus-within:ring-[#4285F4] focus-within:ring-offset-2
      "
      itemScope
      itemType="https://schema.org/Review"
    >
      <header className="flex items-center gap-3">
        {review.avatar ? (
          <img
            src={review.avatar}
            alt=""
            className="w-9 h-9 rounded-full object-cover shrink-0"
            loading="lazy"
            width={36}
            height={36}
          />
        ) : (
          <MonogramAvatar name={review.author} />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span
              className="font-medium text-[#202124] text-sm truncate"
              itemProp="author"
            >
              {review.author}
            </span>
            {review.isLocalGuide && (
              <span className="text-[10px] font-medium text-[#5f6368] bg-[#f1f3f4] rounded px-1.5 py-0.5 uppercase tracking-wide">
                Local Guide
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5 mt-0.5">
            <div
              className="flex gap-0.5"
              role="img"
              aria-label={`${review.rating} out of 5 stars`}
            >
              {Array.from({ length: 5 }, (_, i) => (
                <StarIcon key={i} filled={i < review.rating} />
              ))}
            </div>
            <time
              className="text-xs text-[#5f6368]"
              dateTime={review.datePublished}
              itemProp="datePublished"
            >
              {review.relativeDate}
            </time>
          </div>
        </div>
      </header>

      <p
        className="text-sm text-[#3c4043] leading-relaxed flex-1"
        itemProp="reviewBody"
      >
        {body}
      </p>

      <footer className="flex items-center justify-between pt-2 border-t border-[#f1f3f4]">
        <a
          href={review.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-xs text-[#1967d2] hover:underline font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4285F4] rounded px-1 -mx-1"
          aria-label={`Read ${review.author}'s full review on Google`}
          data-analytics-event="click_review_card"
          data-analytics-location="google_reviews_carousel"
        >
          <GoogleGMark className="w-3.5 h-3.5" title="" />
          Posted on Google
        </a>
      </footer>
    </article>
  );
};

const GoogleReviewsCarousel: React.FC = () => {
  const rating = GOOGLE_REVIEW_STATS.ratingValue.toFixed(1);
  const count = GOOGLE_REVIEW_STATS.totalReviews;
  const jsonLd = useMemo(() => buildGoogleReviewsJsonLd(), []);
  // Duplicate the track so the marquee loop is seamless.
  const track = useMemo(() => [...GOOGLE_REVIEWS, ...GOOGLE_REVIEWS], []);

  return (
    <>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <section
        aria-labelledby="google-reviews-heading"
        className="google-reviews bg-[#f8f9fa] border-y border-[#e8eaed]"
      >
        <div className="container-custom py-6 md:py-8">
          <header className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4 mb-4 md:mb-5">
            <div className="flex items-center gap-3">
              <GoogleGMark className="w-8 h-8 md:w-9 md:h-9" />
              <div>
                <h2
                  id="google-reviews-heading"
                  className="text-lg md:text-xl font-medium text-[#202124] leading-tight"
                  style={{ fontFamily: '"Google Sans","Product Sans",Roboto,system-ui,sans-serif' }}
                >
                  Our Google Reviews
                </h2>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-base md:text-lg font-semibold text-[#202124]">
                    {rating}
                  </span>
                  <div
                    className="flex gap-0.5"
                    role="img"
                    aria-label={`${rating} out of 5 stars on Google`}
                  >
                    {Array.from({ length: 5 }, (_, i) => (
                      <StarIcon key={i} filled />
                    ))}
                  </div>
                  <span className="text-sm text-[#5f6368]">
                    · {count} reviews
                  </span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <a
                href={GOOGLE_REVIEW_STATS.reviewsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-[#1967d2] hover:bg-[#e8f0fe] rounded-md px-3 py-1.5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4285F4] focus-visible:ring-offset-1"
              >
                See all on Google
                <svg
                  aria-hidden="true"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  className="w-3.5 h-3.5"
                >
                  <path d="M11 3h6v6h-2V6.4l-7.3 7.3-1.4-1.4L13.6 5H11V3zM4 5h4v2H6v8h8v-2h2v4H4V5z" />
                </svg>
              </a>
              <a
                href={GOOGLE_REVIEW_STATS.writeReviewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="hidden sm:inline-flex items-center gap-1.5 text-sm font-medium text-white bg-[#1a73e8] hover:bg-[#1967d2] rounded-md px-3 py-1.5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4285F4] focus-visible:ring-offset-1"
              >
                Write a review
              </a>
            </div>
          </header>

          <div
            className="google-reviews__viewport relative overflow-hidden"
            aria-roledescription="carousel"
            aria-label="Customer reviews from Google"
          >
            <div className="google-reviews__track flex">
              {track.map((review, i) => (
                <ReviewCard
                  key={`${review.id}-${i}`}
                  review={review}
                />
              ))}
            </div>

            {/* Gradient fades to hint at horizontal overflow. */}
            <div className="pointer-events-none absolute inset-y-0 left-0 w-10 md:w-16 bg-gradient-to-r from-[#f8f9fa] to-transparent" />
            <div className="pointer-events-none absolute inset-y-0 right-0 w-10 md:w-16 bg-gradient-to-l from-[#f8f9fa] to-transparent" />
          </div>
        </div>
      </section>
    </>
  );
};

export default GoogleReviewsCarousel;
